import { Badge, type BadgeProps } from '@aecom-ai-engineering/react-components';
import { type Invite, InviteStatus } from '@consigli/types';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { PeopleRow, PeopleTable, PeopleTableSkeletonRows } from './people-table';

/**
 * The frame draws no Invites list, so this borrows the Users one: a name-ish
 * first column, a pill, and a date. Pending is the outline pill the frame uses
 * for a plain user, accepted the solid one, rejected the destructive one.
 */
const STATUS_VARIANT: Record<InviteStatus, BadgeProps['variant']> = {
  [InviteStatus.PENDING]: 'outline',
  [InviteStatus.ACCEPTED]: 'primary',
  [InviteStatus.REJECTED]: 'destructive',
};

/**
 * Reuses the `user-management.invite-status.*` keys rather than adding three
 * more: the strings are already translated in all eleven locales and say exactly
 * this. The namespace differs from the rest of this tab, which is why it is
 * spelled out here instead of looking like a typo.
 */
const StatusBadge: FC<{ status: InviteStatus }> = ({ status }) => {
  const { t } = useTranslation();

  return (
    <Badge
      isRound
      label={t(`user-management.invite-status.${status.toLowerCase()}`)}
      variant={STATUS_VARIANT[status]}
    />
  );
};

/**
 * The frame's "08 nov, 2026" is a Figma mock rather than a format to reproduce.
 * Month abbreviation and field order are locale business — en-GB puts the day
 * first, en-US the month — so hardcoding the frame's literal would be wrong in
 * ten of the eleven locales. Intl renders the same three fields the way each
 * locale writes them.
 *
 * Here rather than in a shared util because the Users table's date column has no
 * data behind it yet. It moves out when that column gets a value to format.
 */
const formatInviteDate = (iso: string, language: string): string =>
  new Intl.DateTimeFormat(language, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(iso));

interface ProjectInvitesTableProps {
  invites: Invite[];
  /** Draws placeholder rows under the real header. */
  isLoading?: boolean;
}

/**
 * Invites naming this project.
 *
 * No row actions. There is no resend, revoke or cancel endpoint anywhere in the
 * API client, so every action a row could offer would be a button with nothing
 * behind it.
 *
 * An invite has no recipient name — `recipient` is an email and nothing else —
 * so the name cell carries one line where the Users table carries two.
 */
export const ProjectInvitesTable: FC<ProjectInvitesTableProps> = ({
  invites,
  isLoading = false,
}) => {
  const { t, i18n } = useTranslation();

  return (
    <PeopleTable
      caption={t('project-overview.invites-table-caption')}
      headers={[
        t('project-overview.column-recipient'),
        t('project-overview.column-status'),
        t('project-overview.column-expires-on'),
      ]}
    >
      {isLoading ? (
        <PeopleTableSkeletonRows />
      ) : (
        invites.map((invite) => (
          <PeopleRow
            badge={<StatusBadge status={invite.status} />}
            key={invite.id}
            // Lowercased for display, as the Users table does.
            title={invite.recipient.toLowerCase()}
            trailing={formatInviteDate(invite.expiresAt, i18n.language)}
          />
        ))
      )}
    </PeopleTable>
  );
};
